<template>
  <vetalfa-dialog
    v-on:dialogClose="contentModel = false"
    :title="_getLabel('DRUGS_INTERACTIONS')"
    :toggleModel="contentModel"
    :height="$vuetify.breakpoint.xsOnly ? null : totalHeight"
  >
    <v-stepper flat v-model="stepModel" vertical>
      <v-stepper-step step="1" @click="stepModel = 1" class="vetalfa-pointer">
        <span class="vetalfa-tab-text">Sobre as interações</span>
        <small class="mt-1">Classificações, Avisos e Precauções.</small>
      </v-stepper-step>

      <v-stepper-content step="1" class="px-0 mr-1">
        <vetalfa-section-item
          class="ml-n1"
          :prop="_getLabel('DRUGS_INTERACTIONS_DISCLAIMER_1')"
        ></vetalfa-section-item>
        <v-row class="ma-0 pa-0 my-2 pl-2" justify="start">
          <v-chip class="black--text mr-2" color="success">
            <v-icon small class="mr-2">{{ mdiSpeedometerSlow }}</v-icon>
            Baixa
          </v-chip>
          <v-chip class="black--text mr-2" color="warning">
            <v-icon small class="mr-2">{{ mdiSpeedometerMedium }}</v-icon>
            Moderada
          </v-chip>
          <v-chip class="black--text mr-2" color="error">
            <v-icon small class="mr-2">{{ mdiSpeedometer }}</v-icon>
            Alta
          </v-chip>
        </v-row>
        <vetalfa-section-item
          class="ml-n1"
          :prop="_getLabel('DRUGS_INTERACTIONS_DISCLAIMER_2')"
          :index="1"
        ></vetalfa-section-item>
        <vetalfa-section-item
          class="ml-n1"
          :prop="_getLabel('DRUGS_INTERACTIONS_DISCLAIMER_3')"
          :index="1"
        ></vetalfa-section-item>
        <vetalfa-section-item
          class="ml-n1"
          :prop="_getLabel('DRUGS_INTERACTIONS_DISCLAIMER_4')"
          :index="1"
        ></vetalfa-section-item>
      </v-stepper-content>

      <v-stepper-step step="2" @click="stepModel = 2" class="vetalfa-pointer">
        <span class="vetalfa-tab-text">Seleção de Medicamentos</span>
        <small class="mt-1">Selecione de dois a seis medicamentos.</small>
      </v-stepper-step>

      <v-stepper-content step="2" class="px-0 mr-1">
        <v-row class="ma-0" justify="end">
          <v-autocomplete
            :loading="!drugsSummary || !drugsSummary['drugs']"
            v-model="inputModel"
            :key="inputModel"
            :search-input.sync="search"
            :items="availableDrugs"
            item-text="name"
            return-object
            :placeholder="'Busque o medicamento desejado'"
            :menu-props="{ maxHeight: 200 }"
            persistent-hint
            hide-details
            class="px-2 pa-0"
            :no-data-text="_getLabel('ACTIVE_PRINCIPLES_NO_DATA')"
            item-color="primary"
            @change="search = ''"
            clearable
            outlined
            :filter="customFilter"
            dense
          >
            <template v-slot:item="{ item }">
              <p
                class="
                  ma-0
                  pa-0
                  my-2
                  vetalfa-autocomplete-component-size
                  v-list-item__content v-list-item__title
                "
              >
                {{ item.name.trim() }}
              </p>
            </template>
          </v-autocomplete>
        </v-row>
        <v-row v-if="sortedDrugs.length === 0" class="ma-0 pa-0 py-5">
          <v-col cols="12" justify="center" class="ma-0 pa-0 pt-2 mt-5">
            <v-row justify="center" class="ma-0 pa-0">
              <v-img
                position="center"
                height="50px"
                class="ma-0 pa-0"
                :src="noDrugsImage"
                :lazy-src="noDrugsImage"
                max-width="150"
                contain
              ></v-img>
            </v-row>
          </v-col>
          <v-col cols="12" justify="center" class="ma-0 pa-0 mt-2">
            <p
              class="ma-0 pa-0 text-center vetalfa-card-title-disabled"
              style="font-size: 16px"
            >
              Sem medicamentos
            </p>
          </v-col>
          <v-col cols="12" justify="center" class="ma-0 px-2 pa-0 mb-5">
            <v-row justify="center" class="ma-0 pa-0">
              <p class="ma-0 pa-0 text-center vetalfa-card-text-disabled">
                Quando você adicionar, seus medicamentos vão aparecer aqui.
              </p>
            </v-row>
          </v-col>
        </v-row>
        <v-row v-else class="ma-0 pa-0 px-2 pb-5 my-5">
          <v-chip-group column>
            <v-chip
              outlined
              v-for="(drug, drugIndex) in sortedDrugs"
              :key="drugIndex + drug.name"
              @click="removeDrug(drug)"
              @click:close="removeDrug(drug)"
              close
              color="primary"
              class="vetalfa-chip-component-size py-1 text-left px-3"
            >
              {{ drug.name }}
            </v-chip>
          </v-chip-group>
        </v-row>
        <v-row
          v-if="sortedDrugs.length !== 0"
          class="ma-0 pa-0 my-5"
          justify="end"
        >
          <v-btn
            text
            @click="clearDrugs()"
            color="primary"
            class="text-none text-decoration-underline mb-5"
          >
            <v-icon right color="primary" class="mr-2 pb-1">
              {{ mdiTrashCanOutline }}
            </v-icon>
            Limpar medicamentos
          </v-btn>
        </v-row>
      </v-stepper-content>

      <v-stepper-step @click="stepModel = 3" step="3" class="vetalfa-pointer">
        <span class="vetalfa-tab-text">Resultados das interações</span>
        <small class="mt-1">Interações encontradas e sem registros.</small>
      </v-stepper-step>

      <v-stepper-content step="3" class="ma-0 pa-0">
        <vetalfa-section-title
          prop="Interações encontradas:"
          v-if="foundInteractions.length !== 0"
        ></vetalfa-section-title>
        <v-row class="ma-0 px-6">
          <v-expansion-panels
            flat
            v-for="interaction in foundInteractions"
            :key="`${interaction.firstDrug} ${interaction.secondDrug}`"
            class="ma-0 pa-0 my-2 vetalfa-card-grey-border"
            active-class="vetalfa-card-primary-border"
          >
            <v-expansion-panel>
              <v-expansion-panel-header
                class="ma-0 pa-0 pa-2"
                :expand-icon="mdiMenuDown"
              >
                <v-row class="ma-0 pa-0 pt-4" justify="center">
                  <v-col class="ma-0 pa-0" cols="12">
                    <v-row class="ma-0 pa-0" justify="center">
                      <p class="ma-0 pa-0 vetalfa-card-text font-weight-bold">
                        {{ interaction.firstName }}
                      </p>
                    </v-row>
                  </v-col>
                  <v-col class="ma-0 pa-0 mb-2 mt-1" cols="12">
                    <v-row
                      justify="center"
                      v-if="interaction.level === 1"
                      class="ma-0 pa-0 px-2 my-1"
                    >
                      <v-chip class="black--text" color="success">
                        <v-icon small class="mr-2">{{
                          mdiSpeedometerSlow
                        }}</v-icon>
                        Baixa
                      </v-chip>
                    </v-row>
                    <v-row
                      justify="center"
                      v-else-if="interaction.level === 2"
                      class="ma-0 pa-0 px-2 my-1"
                    >
                      <v-chip class="black--text" color="warning">
                        <v-icon small class="mr-2">{{
                          mdiSpeedometerMedium
                        }}</v-icon>
                        Moderada
                      </v-chip>
                    </v-row>
                    <v-row
                      justify="center"
                      v-else-if="interaction.level === 3"
                      class="ma-0 pa-0 px-2 my-1"
                    >
                      <v-chip class="black--text" color="error">
                        <v-icon small class="mr-2">{{ mdiSpeedometer }}</v-icon>
                        Alta
                      </v-chip>
                    </v-row>
                  </v-col>
                  <v-col class="ma-0 pa-0" cols="12">
                    <v-row class="ma-0 pa-0" justify="center">
                      <p class="ma-0 pa-0 vetalfa-card-text font-weight-bold">
                        {{ interaction.secondName }}
                      </p>
                    </v-row>
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="ma-0 py-2 pb-5">
                <vetalfa-section-item
                  :prop="sentences"
                  :index="index"
                  :key="`sentences${index}`"
                  v-for="(sentences, index) in formatSentences(interaction)"
                >
                </vetalfa-section-item>
                <v-row
                  v-if="interaction.references.length !== 0"
                  class="ma-0 pa-0 mt-3 pl-5"
                  justify="start"
                >
                  <v-btn
                    @click="openReferences(interaction.references)"
                    text
                    color="primary"
                    class="text-none text-decoration-underline ma-0 pa-0"
                  >
                    Ver referências
                  </v-btn>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-row>
        <vetalfa-section-title
          prop="Interações sem dados registrados:"
          v-if="notFoundInteractions.length !== 0"
          class="mt-5"
        ></vetalfa-section-title>
        <vetalfa-section-item
          v-for="(interaction, index) in notFoundInteractions"
          :key="`notFound${index}`"
          :index="index"
          :prop="`${interaction.firstName.trim()} e ${interaction.secondName.trim()};`"
        ></vetalfa-section-item>
        <v-alert
          dense
          v-show="!loading"
          type="info"
          color="primary"
          @click="suggestNewContent"
          class="mt-5 mx-5 px-1 py-2 vetalfa-pointer"
          outlined
        >
          <p class="ma-0 pa-0 vetalfa-subtitle primary--text">
            Não encontrou um resultado esperado?
          </p>
          <p class="ma-0 pa-0 mt-1 vetalfa-subtitle primary--text">
            <span class="text-decoration-underline">Clique aqui</span>
            para sugerir mudanças ou tirar dúvidas.
          </p>
        </v-alert>
      </v-stepper-content>
    </v-stepper>
    <div style="height: 70px; background-color: white"></div>
    <v-row
      class="ma-0 pa-0 white vetalfa-max-width vetalfa-footer"
      align="center"
      justify="space-around"
    >
      <v-col :cols="$vuetify.breakpoint.xsOnly ? 6 : 4" class="ma-0 pa-0">
        <v-row class="ma-0 pa-0 pl-2 pr-1 pb-2" align="center" justify="start">
          <v-btn
            v-show="!loading && stepModel != 1"
            block
            outlined
            class="text-none primary--text"
            @click="stepModel--"
          >
            Voltar
          </v-btn>
        </v-row>
      </v-col>
      <v-spacer></v-spacer>
      <v-col :cols="$vuetify.breakpoint.xsOnly ? 6 : 4" class="ma-0 pa-0">
        <v-row class="ma-0 pa-0 pr-2 pl-1 pb-2" align="center" justify="end">
          <v-btn
            block
            v-show="loading || stepModel < 3"
            :loading="loading"
            class="text-none vetalfa-primary-btn"
            @click="incrementStepModel()"
            light
          >
            {{ stepModel == 2 ? "Ver resultados" : "Continuar" }}
          </v-btn>
        </v-row>
      </v-col>
    </v-row>
    <vetalfa-references-dialog></vetalfa-references-dialog>
  </vetalfa-dialog>
</template>

<script>
import {
  mdiSpeedometerSlow,
  mdiSpeedometerMedium,
  mdiSpeedometer,
  mdiCancel,
  mdiSwapVertical,
  mdiChevronRight,
  mdiPill,
  mdiSwapHorizontal,
  mdiMenuDown,
  mdiTrashCanOutline,
} from "@mdi/js";
import imagesConstants from "@/constants/images.js";
import { mapGetters, mapActions, mapMutations } from "vuex";
import ReferencesDialog from "@/components/ReferencesDialog.vue";
export default {
  mounted() {
    this.$nextTick(() => {
      this.totalHeight = document.getElementById("app").offsetHeight;
    });
  },
  props: {
    triggerModel: {
      default: null,
      type: Number,
    },
  },
  components: {
    "vetalfa-references-dialog": ReferencesDialog,
  },
  methods: {
    ...mapActions("drugs", ["getSummary", "checkInteractions"]),
    ...mapActions("general", ["showError", "showAlert", "logEvent"]),
    ...mapMutations("general", ["setUserSection"]),
    ...mapMutations("topics", ["setClickedReferences"]),
    incrementStepModel() {
      if (this.stepModel == 2 && this.drugs.length < 2) {
        return this.showAlert({
          text: this._getMessage("DRUGS_INTERACTIONS_MIN"),
          color: "black",
        });
      }
      this.stepModel++;
    },
    suggestNewContent() {
      this.logEvent("suggest_content_click");
      this.setUserSection({ comunication: true });
    },
    openReferences(references) {
      this.setClickedReferences(references);
    },
    clearDrugs() {
      this.drugs = [];
      this.interactions = [];

      this.$nextTick(() => {
        this.inputModel = null;
        this.search = "";
      });
    },
    removeDrug(drug) {
      this.drugs = this.drugs.filter((d) => {
        return d.id !== drug.id;
      });
      this.$nextTick(() => {
        this.inputModel = null;
        this.search = "";
      });
    },
    formatSentences({ description, eDescription }) {
      let sentences = [];
      if (!!description) {
        sentences = sentences.concat(
          description.replace(/([.?!])\s*(?=[A-Z])/g, "$1|").split("|")
        );
      }
      if (!!eDescription) {
        sentences = sentences.concat(
          eDescription.replace(/([.?!])\s*(?=[A-Z])/g, "$1|").split("|")
        );
      }
      return sentences;
    },
    customFilter(_item, queryText, itemText) {
      itemText = this._createIdentifier(itemText);
      queryText = this._createIdentifier(queryText);
      return itemText.includes(queryText);
    },
    async checkInteractionsAsync() {
      if (this.drugs.length === 0) {
        this.stepModel = 2;
        return this.showAlert({
          text: this._getMessage("DRUGS_INTERACTIONS_MIN"),
          color: "black",
        });
      }
      try {
        if (this.hasDrugsChangesAfterInteraction) {
          this.interactions = await this.checkInteractions({
            drugs: this.drugs,
          });
        }
        if (this.interactions.length === 0) {
          this.showAlert({
            text: this._getMessage("DRUGS_INTERACTIONS_NOT_FOUND"),
            color: "black",
          });
        }
        this.hasDrugsChangesAfterInteraction = false;
      } catch (err) {
        this.interactions = [];
        this.stepModel = 2;
        this.hasDrugsChangesAfterInteraction = true;
      }
    },
    selectDrug(val) {
      if (this.drugs.length === 6) {
        return this.showAlert({
          text: this._getMessage("DRUGS_INTERACTIONS_MAX"),
          color: "black",
        });
      }
      this.drugs.push(val);
      this.$nextTick(() => {
        this.inputModel = null;
        this.search = "";
      });
    },
    async getSummaryAsync() {
      const collection = "drugs";
      if (
        !this.drugsSummary ||
        !this.drugsSummary[collection] ||
        !this.drugsSummary[collection].length
      ) {
        try {
          await this.getSummary({ collection, temp: false });
        } catch (err) {
          this.showError();
        }
      }
      if (
        ["drugs", "doses"].includes(this.$route.name) &&
        this.category.id === 1 &&
        !!this.drug
      ) {
        setTimeout(
          function () {
            this.stepModel = 2;
            this.drugs.push(this.drug);
          }.bind(this),
          500
        );
      }
    },
  },
  computed: {
    ...mapGetters("drugs", ["drugsSummary", "category", "drug"]),
    ...mapGetters("general", ["loading"]),
    notFoundInteractions() {
      return this.interactions.filter((i) => {
        return i.level === null;
      });
    },
    foundInteractions() {
      return this.interactions.filter((i) => {
        return i.level !== null;
      });
    },
    availableDrugs() {
      const lst = !!this.drugsSummary ? this.drugsSummary["drugs"] || [] : [];
      if (!lst) {
        return [];
      }
      return lst
        .filter((d) => {
          return !this.drugs.includes(d);
        })
        .sort((a, b) =>
          this._removeAccents(a.name) > this._removeAccents(b.name) ? 1 : -1
        );
    },
    sortedDrugs() {
      return this.drugs.sort((a, b) =>
        this._removeAccents(a.name) > this._removeAccents(b.name) ? 1 : -1
      );
    },
  },
  watch: {
    inputModel(val) {
      if (!!val) {
        this.selectDrug(val);
      }
    },
    contentModel(val) {
      if (val) {
        this.getSummaryAsync();
      }
    },
    drugs() {
      this.interactions = [];
      this.hasDrugsChangesAfterInteraction = true;
    },
    stepModel(val) {
      if (val === 3) {
        this.checkInteractionsAsync();
      }
    },
    triggerModel(val) {
      if (val) {
        this.stepModel = 1;
        this.drugs = [];
        this.logEvent("interactions_trigger_model");
        this.contentModel = true;
        this.$nextTick(() => {
          this.totalHeight = document.getElementById("app").offsetHeight;
        });
      }
    },
  },
  data() {
    return {
      screenSize: 768,
      inputModel: null,
      contentModel: false,
      mdiSpeedometerSlow,
      mdiSpeedometerMedium,
      mdiSpeedometer,
      mdiCancel,
      mdiChevronRight,
      mdiSwapVertical,
      isCollapsed: false,
      drugs: [],
      interactions: [],
      search: "",
      hasDrugsChangesAfterInteraction: false,
      mdiPill,
      mdiSwapHorizontal,
      stepModel: 1,
      mdiTrashCanOutline,
      noDrugsImage: imagesConstants.NO_RESULTS,
      totalHeight: 0,
      mdiMenuDown,
    };
  },
};
</script>
